import { useState, type CSSProperties } from 'react';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Icon } from '../../components/ui/Icon';
import { Input } from '../../components/ui/Input';
import { StatusDot } from '../../components/ui/StatusDot';
import type { Actuador } from '../../../domain/entities/Actuador';
import type { EstadoComando } from '../../../domain/value-objects/EstadoComando';
import estilos from './CentroControl.module.css';

const PULSO_POR_DEFECTO = 500;

interface TarjetaActuadorProps {
  actuador: Actuador;
  estado: EstadoComando;
  puedeOperar: boolean;
  motivoSinPermiso: string | null;
  onEncender: () => void;
  onApagar: () => void;
  onPulsar: (ms: number) => void;
}

/**
 * Tarjeta de un actuador con sus controles.
 *
 * Sólo se muestran las órdenes que el equipo admite, y mientras una está en
 * curso los botones se bloquean: enviar dos órdenes seguidas sin saber si la
 * primera llegó es la forma más fácil de dejar un equipo en un estado que
 * nadie pidió.
 */
export function TarjetaActuador({
  actuador,
  estado,
  puedeOperar,
  motivoSinPermiso,
  onEncender,
  onApagar,
  onPulsar,
}: TarjetaActuadorProps) {
  const [pulsoMs, setPulsoMs] = useState(String(PULSO_POR_DEFECTO));

  const encendido = actuador.encendido;
  const bloqueado = !puedeOperar || estado.enCurso;
  const motivo = !puedeOperar ? motivoSinPermiso : estado.enCurso ? 'Hay una orden en curso' : null;

  const estiloOrden = { '--color-orden': estado.color } as CSSProperties;

  return (
    <article className={estilos.tarjeta} style={estiloOrden}>
      <div className={estilos.cabecera}>
        <div style={{ minWidth: 0 }}>
          <div className={estilos.nombre}>{actuador.nombre}</div>
          <div className={estilos.ubicacion}>{actuador.ubicacion}</div>
        </div>
        <Badge color="var(--text-2)">{actuador.tipo}</Badge>
      </div>

      <div className={estilos.estadoActual} title={`Origen: ${actuador.origenEstado}`}>
        <span className={estilos.estadoEtiqueta}>{actuador.origenEstado}</span>
        {/*
          Encendido y apagado se destacan igual, cada uno con su color: que un
          equipo esté apagado también es información que interesa ver de un
          vistazo, no una ausencia de información. El gris apagado queda sólo
          para cuando el equipo todavía no ha reportado nada.
        */}
        <StatusDot
          color={
            encendido === null
              ? 'var(--text-3)'
              : encendido
                ? 'var(--status-success)'
                : 'var(--status-danger)'
          }
          encendido={encendido !== null}
          texto={actuador.estadoMostrable}
          tamano={10}
        />
      </div>

      {actuador.sinAcciones ? (
        <div className={estilos.lineaOrden}>
          <Icon nombre="infoContorno" tamano={14} />
          Este actuador no admite órdenes desde la consola
        </div>
      ) : (
        <>
          <div className={estilos.controles}>
            {actuador.admiteEncendido && (
              <Button
                variante="exito"
                tamano="sm"
                icono="encender"
                onClick={onEncender}
                motivoDeshabilitado={motivo}
              >
                Encender
              </Button>
            )}
            {actuador.admiteApagado && (
              <Button
                variante="peligro"
                tamano="sm"
                icono="apagar"
                onClick={onApagar}
                motivoDeshabilitado={motivo}
              >
                Apagar
              </Button>
            )}
          </div>

          {actuador.admitePulso && (
            <div className={estilos.pulso}>
              <div className={estilos.pulsoCampo}>
                <Input
                  etiqueta="Pulso (ms)"
                  type="number"
                  min={50}
                  max={30000}
                  step={50}
                  value={pulsoMs}
                  onChange={(e) => setPulsoMs(e.target.value)}
                  disabled={bloqueado}
                />
              </div>
              <Button
                variante="secundario"
                tamano="sm"
                icono="pulso"
                onClick={() => onPulsar(Number(pulsoMs))}
                motivoDeshabilitado={motivo}
              >
                Pulsar
              </Button>
            </div>
          )}
        </>
      )}

      <div className={estilos.lineaOrden}>
        {estado.enCurso ? (
          <Icon nombre="temporizador" tamano={14} />
        ) : estado.valor === 'aplicado' ? (
          <Icon nombre="exito" tamano={14} />
        ) : estado.fallo ? (
          <Icon nombre="errorContorno" tamano={14} />
        ) : (
          <Icon nombre="circulo" tamano={14} />
        )}
        {estado.etiqueta}
      </div>
    </article>
  );
}
